import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  NativeSelect,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import CloseIcon from '@mui/icons-material/Close';

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'sales', label: 'Sales' },
  { value: 'labor', label: 'Labor' },
  { value: 'tips', label: 'Tips' },
  { value: 'cogs', label: 'COGS' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Revoked' },
];

const CompactInput = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    borderRadius: 6,
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #dde1e6',
    fontSize: 13,
    padding: '6px 10px',
    fontFamily: 'Lato, sans-serif',
    '&:focus': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const ExceptionsLogDialog = ({ open, onClose, firebaseUser }) => {
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceNames, setSourceNames] = useState({});

  const fetchSourceNames = async () => {
    try {
      const token = await firebaseUser.getIdToken(true);
      const res = await axios.get('/hd-api/data-integrity-2/source-names', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSourceNames(res.data.names || {});
    } catch (err) {
      console.error('Error fetching source names:', err);
    }
  };

  const fetchExceptions = async (category, isActive) => {
    try {
      setLoading(true);
      setError(null);
      const token = await firebaseUser.getIdToken(true);

      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (isActive) params.set('isActive', isActive);

      const response = await axios.get(
        `/hd-api/data-integrity-2/exceptions/log?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setExceptions(response.data.exceptions || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching exceptions log:', err);
      setError('Failed to load exceptions log.');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && firebaseUser) {
      fetchSourceNames();
      fetchExceptions(categoryFilter, statusFilter);
    }
  }, [open, categoryFilter, statusFilter]);

  const handleClose = () => {
    setCategoryFilter('');
    setStatusFilter('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          fontFamily: 'Lato',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        Exceptions Log
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Filter row */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          <NativeSelect
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            input={<CompactInput />}
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </NativeSelect>

          <NativeSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            input={<CompactInput />}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </NativeSelect>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ py: 4, textAlign: 'center' }}>
            {error}
          </Typography>
        ) : exceptions.length === 0 ? (
          <Typography
            color="text.secondary"
            sx={{ py: 4, textAlign: 'center' }}
          >
            No exceptions found.
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontFamily: 'Lato' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontFamily: 'Lato' }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontFamily: 'Lato' }}>Source</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontFamily: 'Lato' }}>Store</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontFamily: 'Lato' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontFamily: 'Lato' }}>Comment</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontFamily: 'Lato' }}>Acknowledged By</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontFamily: 'Lato' }}>Acknowledged At</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontFamily: 'Lato' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exceptions.map((ex) => (
                  <TableRow key={ex.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{ex.businessDate}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{ex.categoryKey}</TableCell>
                    <TableCell>{sourceNames[ex.sourceKey] || ex.sourceKey}</TableCell>
                    <TableCell>{ex.storeId}</TableCell>
                    <TableCell>{ex.anomalyType}</TableCell>
                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ex.comment}
                    </TableCell>
                    <TableCell>{ex.acknowledgedBy}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {ex.acknowledgedAt
                        ? new Date(ex.acknowledgedAt).toLocaleString()
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ex.isActive ? 'Active' : 'Revoked'}
                        size="small"
                        color={ex.isActive ? 'success' : 'default'}
                        variant={ex.isActive ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 600, fontSize: 11 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExceptionsLogDialog;
